/**
 * Immutable collection for maintains item order for any subset of reservationRooms, services and deposits
 * Expected order is:
 *   1. reservationRooms & reservation room Services (interleaved)
 *   2. reservation services
 *   3. deposits
 */
export class ItemsCollection {
    /**
     * @param {[('reservationRoom'|'service'|'deposit'), object]}[] items
     */
    constructor(items, selected = []) {
        this.items = items;
        this.selected = new Set(selected);
    }

    static fromUnordered(unorderedItems) {
        if (unorderedItems.length === 0) {
            return emptyCollection;
        }

        const reservationRooms = {};
        const reservationServices = [];
        const deposits = [];

        unorderedItems.forEach(element => group(element, reservationRooms, reservationServices, deposits));

        const items = Object
            .entries(reservationRooms)
            .flatMap(element => {
                if (element[1].reservationRoom) {
                    return [element[1].reservationRoom, ...element[1].services];
                } else {
                    return element[1].services;
                }
            })
            .concat(reservationServices, deposits);

        return new ItemsCollection(items);
    }

    /**
     * @return {[ItemsCollection, ItemsCollection]}
     */
    partition(predicate) {
        const itemsLeft = [];
        const itemsRight = [];

        const selectedLeft = new Set();
        const selectedRight = new Set();

        for (let i = 0; i < this.items.length; ++i) {
            const element = this.items[i];
            const isSelected = this.selected.has(element[1]);
            if (predicate(element[0], element[1], i, isSelected)) {
                itemsLeft.push(element);
                if (isSelected) {
                    selectedLeft.add(element[1]);
                }
            } else {
                itemsRight.push(element);
                if (isSelected) {
                    selectedRight.add(element[1]);
                }
            }
        }

        if (itemsRight.length === 0) {
            return [this, emptyCollection];
        } else if (itemsLeft.length === 0) {
            return [emptyCollection, this];
        } else {
            return [new ItemsCollection(itemsLeft, selectedLeft), new ItemsCollection(itemsRight, selectedRight)];
        }
    }

    /**
     * @param {ItemsCollection} other
     * @return {ItemsCollection}
     */
    union(other) {
        if (other.items.length === 0) {
            return this;
        } else if (this.items.length === 0) {
            return other;
        }

        const reservationRooms = {};
        const reservationServices = [];
        const deposits = [];

        this.items.forEach(element => group(element, reservationRooms, reservationServices, deposits));
        other.items.forEach(element => group(element, reservationRooms, reservationServices, deposits));

        const items = Object
            .entries(reservationRooms)
            .flatMap(element => {
                if (element[1].reservationRoom) {
                    return [element[1].reservationRoom, ...element[1].services];
                } else {
                    return element[1].services;
                }
            })
            .concat(reservationServices, deposits);

        return new ItemsCollection(items, Array.from(this.selected).concat(Array.from(other.selected)));
    }

    /**
     * @return {ItemsCollection}
     */
    select(predicate) {
        let anyChange = false;
        const newSelected = new Set();

        this.items.forEach(element => {
            if (predicate(element)) {
                newSelected.add(element[1]);
                if (!anyChange && !this.selected.has(element[1])) {
                    anyChange = true;
                }
            } else if (this.selected.has(element[1])) {
                anyChange = true;
            }
        });

        if (!anyChange) {
            return this;
        }

        return new ItemsCollection(this.items, newSelected);
    }

    isSelected(item) {
        return this.selected.has(item);
    }

    get total() {
        return parseFloat(this.items.reduce((carry, item) => {
            if (item[0] === 'reservationRoom') {
                return carry + item[1].gross;
            } else if (item[0] === 'service') {
                return carry + item[1].gross;
            } else if (item[0] === 'deposit') {
                return carry - item[1].amount;
            } else {
                return carry;
            }
        }, 0.0).toFixed(2));
    }
}

function group(element, reservationRooms, reservationServices, deposits) {
    if (element[0] === 'reservationRoom') {
        reservationRooms[element[1].id] = reservationRooms[element[1].id] || {
            reservationRoom: null,
            services: [],
        };
        reservationRooms[element[1].id].reservationRoom = element;
    } else if (element[0] === 'service') {
        if (element[1].reference.reservationRoom) {
            reservationRooms[element[1].reference.reservationRoom.id] = reservationRooms[element[1].reference.reservationRoom.id] || {
                reservationRoom: null,
                services: [],
            };
            reservationRooms[element[1].reference.reservationRoom.id].services.push(element);
        } else {
            reservationServices.push(element);
        }
    } else if (element[0] === 'deposit') {
        deposits.push(element);
    }
}

// Micro-optimisation for maintaining referential stability when there are no items
const emptyCollection = new ItemsCollection(Object.freeze([]));
